import { useField } from 'formik'
import * as React from 'react'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useTheme } from 'src/state/providers/Theme'

import { Editor } from '@tinymce/tinymce-react'
import tinymce from 'tinymce'

import 'tinymce/icons/default'
import 'tinymce/models/dom'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/code'
import 'tinymce/plugins/image'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/media'
import 'tinymce/plugins/table'
import 'tinymce/plugins/wordcount'
import 'tinymce/themes/silver'

import {
  getCountFromHtml,
  getCounterSuffix,
  getMinMaxValidationError,
  getRequiredError,
  stripEscapedWordComments,
  type TranslateFn,
  type TranslatePairFn,
} from 'src/helpers/fns'
import contentCSS from 'src/sass/_dark-theme.scss?raw'
import oxideDark from 'tinymce/skins/ui/oxide-dark/skin.min.css?raw'
import oxide from 'tinymce/skins/ui/oxide/skin.min.css?raw'
import { useFormData } from './formik/Form'

// @ts-expect-error todo
window.tinymce = tinymce

interface Props {
  readonly name: string
  readonly required?: boolean
  readonly label?: string
  readonly disabled?: boolean
  readonly minLength?: number
  readonly maxLength?: number
  readonly countMode?: 'characters' | 'words'
}

const EDITOR_INIT = {
  branding: false,
  height: 300,
  menubar: false,
  skin: false,
  toolbar_mode: 'sliding' as const,
  plugins: ['table', 'link', 'lists', 'media', 'image', 'anchor', 'code'] as string[],
  toolbar:
    'bold italic underline | forecolor backcolor | blocks fontfamily fontsize | undo redo | bullist numlist | alignleft aligncenter alignright alignjustify | link image media table | removeformat code',
  font_size_formats: '10pt 12pt 14pt 15pt 18pt 24pt 36pt',
}

export default function FormikTinyMce({
  required = false,
  minLength,
  maxLength,
  countMode = 'words',
  ...props
}: Props): React.ReactElement {
  const theme = useTheme()
  const t = useTranslatable()
  const isWordMode = countMode === 'words'

  const validate = React.useCallback(
    (value: string): string | undefined => {
      const requiredError = getRequiredError({ required, label: props.label, t: t as TranslateFn })
      if (required && !value) return requiredError

      const count = getCountFromHtml(value, isWordMode)
      return getMinMaxValidationError({
        count,
        minLength,
        maxLength,
        isWordMode,
        t: t as TranslatePairFn,
      })
    },
    [required, props.label, t, isWordMode, minLength, maxLength]
  )

  const [field, meta, helpers] = useField({ name: props.name, validate })

  const [formData] = useFormData()
  const errors = formData.errors.fields[props.name] ?? []

  const lastEmittedValueRef = React.useRef<string | null>(null)

  const handleEditorChange = React.useCallback(
    (value: string) => {
      lastEmittedValueRef.current = value
      void helpers.setValue(value)
    },
    [helpers]
  )

  // A value the editor did not just emit came from outside — an API load or a form
  // reset — where legacy Word HTML carries entity-escaped comment markers. Cleaning
  // only that direction keeps the editor's own output untouched while typing.
  const editorValue = React.useMemo(() => {
    if (typeof field.value !== 'string') return field.value
    return field.value === lastEmittedValueRef.current ? field.value : stripEscapedWordComments(field.value)
  }, [field.value])

  const currentCount = React.useMemo(() => getCountFromHtml(field.value, isWordMode), [field.value, isWordMode])
  const showCounter = minLength != null || maxLength != null
  const isOverLimit = maxLength != null && currentCount > maxLength
  const isUnderMin = minLength != null && currentCount < minLength && currentCount > 0
  const countStatusInvalid = isOverLimit || isUnderMin

  const counterSuffix = getCounterSuffix({ minLength, maxLength, isWordMode, t: t as TranslatePairFn })

  const contentStyle = theme === 'dark' ? `body { color: white };${contentCSS}` : ''
  const init = React.useMemo(
    () => ({
      ...EDITOR_INIT,
      content_style: contentStyle,
      disabled: props.disabled,
    }),
    [contentStyle, props.disabled]
  )

  return (
    <div className="mb-4 p-0">
      {props.label != null && (
        <label className="mb-[10px] flex font-bold text-primaryTextColor" htmlFor={props.name}>
          {props.label}{' '}
          {required && (
            <span className="ml-1 text-primaryRed" data-testid={`${props.name}.required`}>
              *
            </span>
          )}
        </label>
      )}
      <style>{theme === 'dark' ? oxideDark : oxide}</style>
      <Editor
        value={editorValue}
        onEditorChange={handleEditorChange}
        key={theme}
        disabled={props.disabled}
        init={init}
      />
      {showCounter && (
        <div className="mt-1 flex justify-end text-xs text-primaryTextColor">
          <span className={countStatusInvalid ? 'text-primaryRed' : undefined}>
            {currentCount}
            {counterSuffix}
          </span>
        </div>
      )}
      {meta.touched && meta.error != null && (
        <div className="mt-2 text-[13px] text-errorMessages" role="alert">
          {meta.error}
        </div>
      )}
      {errors.map((error, i) => (
        <div key={i} className="mt-2 text-[13px] text-errorMessages">
          {error}
        </div>
      ))}
    </div>
  )
}
